import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import property from 'lodash/property';
import BackgroundColor from '~/client/components/background-color/background-color';

import {
  stringSelectSpec,
  boolCheckboxSpec,
  stringInputSpec,
  numberInputSpec,
} from '../../lib/editing-configs';

import './divider.less';

const Divider = ({
  borderColor,
  bgOptions,
  borderWidth,
  fullWidthMobile,
  backgroundColorHex,
  desktopMaxWidth,
  tabletMaxWidth,
  mobileMaxWidth,
  size,
}) => {
  const dividerClassName = fullWidthMobile
    ? 'standard-divider__cont--100'
    : 'standard-divider__cont';
  let backgroundOptions = bgOptions;
  if (!bgOptions && borderColor) {
    backgroundOptions = { backgroundColor: borderColor };
  }

  const getWidth = () => {
    const type = {
      lg: Number(desktopMaxWidth),
      md: Number(tabletMaxWidth),
      sm: Number(mobileMaxWidth),
    };
    return type[size] || desktopMaxWidth;
  };

  const dividerInnerContent = () => (
    <BackgroundColor bgOptions={backgroundOptions}>
      {(backgroundColor) => (
        <div className={`${dividerClassName}`} style={{ width: getWidth() }}>
          <div
            className="standard-divider"
            style={{
              borderTop: `${borderWidth} solid ${backgroundColor}`,
            }}
          />
        </div>
      )}
    </BackgroundColor>
  );

  return (
    <div
      id="outer-background"
      style={{
        backgroundColor: backgroundColorHex,
      }}
    >
      {dividerInnerContent()}
    </div>
  );
};

Divider.propTypes = {
  borderColor: PropTypes.string,
  borderWidth: PropTypes.string,
  fullWidthMobile: PropTypes.bool,
  bgOptions: PropTypes.object,
  backgroundColorHex: PropTypes.string,
  desktopMaxWidth: PropTypes.number,
  tabletMaxWidth: PropTypes.number,
  mobileMaxWidth: PropTypes.number,
  size: PropTypes.string,
  children: PropTypes.node,
};

Divider.defaultProps = {
  borderColor: 'charcoal',
  borderWidth: '1px',
  fullWidthMobile: false,
  children: null,
  desktopMaxWidth: null,
  tabletMaxWidth: null,
  mobileMaxWidth: null,
};

const getBgColorConfig = (componentDocument) =>
  BackgroundColor.getEditingConfig(componentDocument).map((item) => {
    if (item.fieldName === 'bgOptions.backgroundColor') {
      return {
        ...item,
        fieldLabel: 'Color',
      };
    }
    if (item.fieldName === 'bgOptions.hexColor') {
      return {
        ...item,
        fieldLabel: 'Hex color override',
      };
    }
    return item;
  });

Divider.getEditingConfig = (componentDocument) => [
  stringInputSpec('backgroundColorHex', 'Background Color', {
    help: 'HexColor Value',
  }),
  numberInputSpec('desktopMaxWidth', 'Desktop Max Width Override'),
  numberInputSpec('tabletMaxWidth', 'Tablet Max Width Override'),
  numberInputSpec('mobileMaxWidth', 'Mobile Max Width Override'),
  ...getBgColorConfig(componentDocument),
  stringSelectSpec(
    'borderWidth',
    'Height of divider (in pixels)',
    [
      { label: '1px', value: '1px' },
      { label: '2px', value: '2px' },
      { label: '3px', value: '3px' },
      { label: '4px', value: '4px' },
    ],
    {
      help: 'Select a divider height between 1px and 4px',
    }
  ),
  boolCheckboxSpec(
    'fullWidthMobile',
    '100% width in mobile and desktop viewports?',
    {
      help: 'The Divider defaults to a width of 280px on mobile viewports. Check this box to make it 100% width for mobile and desktop viewports instead.',
    }
  ),
];

Divider.displayName = 'Divider';

export default connect(
  createStructuredSelector({
    size: property('mediaQuery'),
  })
)(Divider);
